import { getQueryClient } from '@/utils/auth';
import client, { routes } from '@/utils/axios';
import { acquisition, clearArrival, referralCode } from '@/utils/referral';
import { ACCESS_TOKEN, DEMO_MODE, REFRESH_TOKEN } from '@/utils/utils';

/// Réglages du poste, pas du compte : ils survivent à la déconnexion. Tout
/// effacer rouvrait chaque aide déjà écartée (« Ne plus afficher ») et
/// remettait la langue par défaut.
const DEVICE_KEYS: StoreKeys[] = [
  'lang',
  'sidebar_shrink',
  'watchlist-migration-ack',
];

export class AuthService {
  static async sendCode({ email }: { email: string }) {
    return (
      await client.post(routes.auth.sendCode, {
        email,
      })
    ).data;
  }

  static async signUp({
    email,
    username,
    password,
    affiliationCode,
  }: {
    email: string;
    username: string;
    password: string;
    affiliationCode?: string;
  }) {
    const res = (
      await client.post(routes.auth.signup, {
        email,
        username,
        password,
        affiliationCode,
        ...acquisition(),
      })
    ).data;
    clearArrival();
    return res;
  }

  static async signinFirebase({ idToken }: { idToken: string }) {
    const req = (
      await client.post<{ refreshToken: string; accessToken: string }>(
        routes.auth.signinFirebase,
        {
          idToken,
          // Ne sert qu'à la création du compte : le code du lien d'arrivée.
          affiliationCode: referralCode(),
          ...acquisition(),
        },
      )
    ).data;
    window.store.set(ACCESS_TOKEN, req.accessToken);
    window.store.set(REFRESH_TOKEN, req.refreshToken);
    clearArrival();
    return req;
  }

  static async signinCode({
    browserCode,
    emailCode,
  }: {
    browserCode: string;
    emailCode: string;
  }) {
    const req = (
      await client.post<{
        refreshToken: string;
        accessToken: string;
      }>(routes.auth.signinCode, {
        emailCode,
        browserCode,
      })
    ).data;
    if (req.accessToken) {
      window.store.set(ACCESS_TOKEN, req.accessToken);
      window.store.set(REFRESH_TOKEN, req.refreshToken);
      // Une vraie connexion sort de la démonstration : sans cela le bandeau
      // « lecture seule » et le gel des commandes suivaient un client payant.
      window.store.set(DEMO_MODE, 'false');
    }
    return req;
  }

  static async signin({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) {
    const req = (
      await client.post<{
        refreshToken?: string;
        accessToken?: string;
        loginCode?: string;
      }>(routes.auth.signin, {
        email,
        password,
      })
    ).data;
    if (req.accessToken) {
      window.store.set(ACCESS_TOKEN, req.accessToken);
      window.store.set(REFRESH_TOKEN, req.refreshToken);
      // Une vraie connexion sort de la démonstration : sans cela le bandeau
      // « lecture seule » et le gel des commandes suivaient un client payant.
      window.store.set(DEMO_MODE, 'false');
    }
    return req;
  }

  /**
   * En démonstration, la déconnexion n'a pas de sens : le cadre du site vitrine
   * n'a pas d'écran de connexion à offrir, il n'en resterait qu'une page morte.
   * L'interface grise déjà le bouton ; ceci ferme le cas d'un appel direct.
   */
  static async signout() {
    if (window.store.get(DEMO_MODE) === 'true') return;
    const kept = DEVICE_KEYS.map((key) => [key, window.store.get(key)] as const);
    window.store.clear();
    for (const [key, value] of kept) if (value !== null) window.store.set(key, value);
    getQueryClient().clear();
    window.location.reload();
  }

  static async resetPassword({
    token,
    password,
  }: {
    token: string;
    password: string;
  }) {
    return (
      await client.post(routes.auth.resetPassword, {
        token,
        password,
      })
    ).data;
  }

  static async forgotPassword({ email }: { email: string }) {
    return (
      await client.post(routes.auth.forgotPassword, {
        email,
      })
    ).data;
  }
}
